/**
 * Tour anchor ids — the shared vocabulary between the elements being
 * highlighted and the step catalogue that highlights them.
 *
 * Anchoring works by putting an `id` prop on an existing element:
 *
 *   <NavItem id={TOUR_ANCHORS.navPlans} … />
 *
 * On web (and therefore Electron, which runs the same bundle) react-native-web
 * maps `id` straight through to the DOM `id`, so the overlay can find the live
 * element with `document.getElementById` and read its real on-screen rect. On
 * native the prop is inert — nothing renders the tour there, and nothing about
 * the anchored component changes.
 *
 * That is deliberately the *whole* mechanism. A ref registry would mean every
 * anchored component re-renders when the tour mounts and has to thread a ref
 * through its own props; an `id` string costs the anchored component nothing
 * and survives the component being unmounted and remounted by navigation.
 *
 * Adding an anchor: add the constant here, put the `id` on the element, then
 * add a step in `steps.ts`. `npm test -- tour` fails if a step points at an
 * anchor that no longer exists in this file.
 */

export const TOUR_ANCHORS = {
  // ── Desktop sidebar (always present on desktop/web ≥ breakpoint) ──────────
  /** The sidebar rail itself — the "here is your navigation" opener. */
  sidebar: 'tour-sidebar',
  /** Ellie orb / AI assistant row at the top of the account-level sidebar. */
  navEllie: 'tour-nav-ellie',
  /** Account-level Plans entry. */
  navPlans: 'tour-nav-plans',
  /** Settings entry, pinned to the bottom in every sidebar mode. */
  navSettings: 'tour-nav-settings',

  // ── Plans list ───────────────────────────────────────────────────────────
  /** "New Plan" button in the Plans header. */
  newPlan: 'tour-new-plan',

  // ── Inside a plan ────────────────────────────────────────────────────────
  /** Setup-checklist card on the plan home screen. */
  planSetupCard: 'tour-plan-setup-card',
  /** Project-level Ellie entry in the sidebar. */
  navProjectEllie: 'tour-nav-project-ellie',
  /** Project-level Docs category in the sidebar. */
  navProjectDocs: 'tour-nav-project-docs',
  /** Project-level Team category in the sidebar. */
  navProjectTeam: 'tour-nav-project-team',

  // ── Settings ─────────────────────────────────────────────────────────────
  /** The "Take a tour" row in Settings → About — where the tour lives after
   *  the sidebar launcher, and the final stop of the tour itself. */
  settingsTourRow: 'tour-settings-row',
} as const;

export type TourAnchorId = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS];
